import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { whitespaceValidator } from '@vendasta/forms';
import { PipelineSettingsService } from '../pipeline-settings.service';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { PipelineInterface, StageInterface } from '@vendasta/sales-opportunities';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { map, switchMap } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

function DisplayPercentageToStored(display: number): number {
  return display / 100;
}

function StoredPercentageToDisplay(stored: number): number {
  return stored * 100;
}
@Component({
  selector: 'sales-ui-pipeline-settings-form',
  templateUrl: './pipeline-settings-form.component.html',
  styleUrls: ['./pipeline-settings-form.component.scss'],
  standalone: false,
})
export class PipelineSettingsFormComponent {
  protected readonly pipelineForm = new FormGroup({
    stages: new FormArray<FormGroup>([]),
    name: new FormControl('', {
      validators: whitespaceValidator(),
    }),
  });
  protected readonly currentPipeline$: Observable<PipelineInterface>;
  private readonly savingChanges$$ = new BehaviorSubject(false);
  protected readonly savingChanges$ = this.savingChanges$$.asObservable();
  private readonly resetRequests$$ = new BehaviorSubject(false);
  private readonly pendingChanges$$ = new BehaviorSubject(false);

  constructor(
    private readonly pipelineSettingsService: PipelineSettingsService,
    private readonly snackbarService: SnackbarService,
    private readonly confirmationModal: OpenConfirmationModalService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.pipelineForm.valueChanges.pipe(takeUntilDestroyed()).subscribe((_) => this.pendingChanges$$.next(true));
    this.currentPipeline$ = this.pipelineSettingsService.currentPipeline$;
    combineLatest([this.currentPipeline$, this.resetRequests$$])
      .pipe(takeUntilDestroyed())
      .subscribe(([pipeline, _]) => {
        if (pipeline?.pipelineId) {
          this.setFormValues(pipeline);
        }
      });
    this.activatedRoute.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.pipelineSettingsService.setPipelineId(params['pipelineId']);
    });
  }

  private setFormValues(pipeline: PipelineInterface): void {
    this.pipelineForm.controls.name.setValue(pipeline.name, { emitEvent: false });
    this.stages.clear();
    pipeline.stages
      .sort((a, b) => (a.probability < b.probability ? -1 : 1))
      .forEach((stage) => {
        const newStage = new FormGroup({
          name: new FormControl(stage.name, whitespaceValidator()),
          probability: new FormControl(StoredPercentageToDisplay(stage.probability), [
            Validators.min(0),
            Validators.max(99),
          ]),
          id: new FormControl(stage.stageId),
        });
        this.stages.push(newStage, { emitEvent: false });
      });
    this.pendingChanges$$.next(false);
  }

  protected get stages(): FormArray<FormGroup> {
    return this.pipelineForm.controls['stages'] as FormArray;
  }

  protected addStage(): void {
    const newStage = new FormGroup({
      name: new FormControl('', whitespaceValidator()),
      probability: new FormControl(0, [Validators.min(0), Validators.max(99)]),
      id: new FormControl(''),
    });
    this.stages.push(newStage);
  }

  protected deleteDisplayStage(stageIndex: number): void {
    this.stages.removeAt(stageIndex);
    this.pipelineForm.markAsDirty();
  }

  protected async setPipelineName(pipelineId: string): Promise<void> {
    if (this.pipelineForm.controls.name.valid && this.pipelineForm.controls.name.dirty) {
      const newName = this.pipelineForm.controls.name.value;
      const success = await firstValueFrom(this.pipelineSettingsService.updatePipelineName(pipelineId, newName));
      if (!success) {
        this.snackbarService.openErrorSnack(
          'FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.PIPELINE_NAME_UPDATE_FAILED',
        );
      }
    }
  }

  protected async setStages(pipelineId: string, stages: StageInterface[]): Promise<void> {
    const success = await firstValueFrom(this.pipelineSettingsService.setStages(pipelineId, stages));
    if (!success) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.PIPELINE_UPDATED');
    }
  }

  private buildUpdates(currentPipeline: PipelineInterface): Promise<void>[] {
    const promises: Promise<void>[] = [];

    const updatedStages: StageInterface[] = this.stages.controls.map((stage) => {
      const nameForm = stage.get('name');
      const probForm = stage.get('probability');
      const id = stage.get('id')?.value || '';
      return {
        stageId: id,
        name: nameForm.value,
        probability: DisplayPercentageToStored(probForm.value),
      };
    });

    promises.push(this.setStages(currentPipeline.pipelineId, updatedStages));
    promises.push(this.setPipelineName(currentPipeline.pipelineId));

    return promises;
  }

  protected async saveChanges(): Promise<void> {
    if (this.pipelineForm.valid && this.pipelineForm.dirty) {
      this.savingChanges$$.next(true);
      const currentPipeline = await firstValueFrom(this.currentPipeline$);
      await Promise.all(this.buildUpdates(currentPipeline));
      this.pipelineSettingsService.requestReload();
      this.pendingChanges$$.next(false);
      this.savingChanges$$.next(false);
    } else if (this.pipelineForm.dirty) {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.FORM_INVALID');
    }
  }

  protected async deletePipeline(pipelineId: string): Promise<void> {
    const proceed = await firstValueFrom(
      this.confirmationModal.openModal({
        type: 'warn',
        title: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.DELETE_TITLE',
        message: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.DELETE_MESSAGE',
        confirmButtonText: 'FRONTEND.SALES_UI.COMMON.ACTION_LABELS.DELETE',
        cancelButtonText: 'FRONTEND.SALES_UI.COMMON.ACTION_LABELS.CANCEL',
        actionOnEnterKey: false,
      }),
    );
    if (proceed) {
      const success = await firstValueFrom(this.pipelineSettingsService.deletePipeline(pipelineId));
      if (success) {
        this.snackbarService.openSuccessSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.DELETE_SUCCESS');
        this.pipelineSettingsService.requestReload();
      } else {
        this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.DELETE_FAILED');
      }
    }
  }
  protected cancelChanges(): void {
    this.resetRequests$$.next(true);
  }

  canDeactivate(): Observable<boolean> {
    return this.pendingChanges$$.pipe(
      switchMap((pendingChanges) => {
        if (pendingChanges) {
          return this.confirmationModal
            .openModal({
              type: 'warn',
              title: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.UNSAVED_CHANGES_TITLE',
              message: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.UNSAVED_CHANGES_MESSAGE',
              confirmButtonText: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.UNSAVED_CHANGES_CONFIRM',
            })
            .pipe(map((confirmation) => !!confirmation));
        } else {
          return of(true);
        }
      }),
    );
  }
}
